import Hls, { type LoaderStats } from "hls.js";
import { sorted } from "helpers";
import { RefObject, useCallback, useEffect, useRef } from "react";
import {
  CameraAttributes,
  CameraPlayback,
  recordPageDownload,
  REPLAY_BUCKET_SECONDS,
  ReplayBucketSeconds,
  VideoPlaybackReport,
} from "./videoPlaybackReport";

export interface VideoPlaybackContext {
  state: "playing" | "paused" | "buffering";
  report: VideoPlaybackReport;
  takeHoldCauseMs: () => number;
}

const emptyMeasurements = () => ({
  sharedHoldMs: 0,
  sharedHoldCount: 0,
  seekMs: [] as number[],
  seekAbandonedCount: 0,
  seekBackwardCount: 0,
  seekBackwardUnplayedCount: 0,
  seekBackwardReplaySeconds: Object.fromEntries(
    REPLAY_BUCKET_SECONDS.map((seconds) => [seconds, 0])
  ) as Record<ReplayBucketSeconds, number>,
  firstFrameMs: [] as number[],
  startupWaitMs: [] as number[],
  segmentCount: 0,
  segmentBytes: 0,
  segmentLoadMs: 0,
  segmentFirstDataMs: 0,
});

export const useVideoPlaybackTelemetry = ({
  videoRef,
  srcUrl,
  playbackContext,
  playbackRate,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  srcUrl: string;
  playbackContext: VideoPlaybackContext | undefined;
  playbackRate: number;
}) => {
  const contextRef = useRef({ playbackContext, playbackRate });
  contextRef.current = { playbackContext, playbackRate };
  const sessionRef = useRef<{
    updateContext: () => void;
    trackSeek: (target: number) => void;
    observeHls: (hls: Hls) => void;
  } | null>(null);
  const enabled = playbackContext !== undefined;

  useEffect(() => {
    const video = videoRef.current;
    const initialContext = contextRef.current.playbackContext;
    if (!video || !srcUrl || !initialContext) return;
    const capabilities: Partial<HTMLVideoElement> = video;
    const source = new URL(srcUrl, window.location.href);
    const camera = source.pathname.split("/video/")[1]?.replace(/\/[^/]+$/, "");
    const attributes: CameraAttributes = {
      tenant: source.pathname.split("/scrap_eyes/")[1]?.split("/")[0],
      "video.page": window.location.pathname.split("/").slice(2, 4).join("/"),
      "video.surface": video.closest('[role="dialog"]') ? "dialog" : "page",
      "video.viewer_timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    let context = {
      ...initialContext,
      playbackRate: contextRef.current.playbackRate,
    };
    let measurements = emptyMeasurements();
    let startupStartedAt = document.hidden ? null : performance.now();
    let seek: { startedAt: number; fromTime: number; target: number } | null =
      null;
    let holdStartedAt: number | null = null;
    // Tracked ourselves, rather than read from `video.played`, because hls.js
    // clears `video.played` whenever it reattaches the media element (a
    // fatal-error remount, or a buffer budget change), which would otherwise
    // make already-watched footage look unplayed after either event.
    let playedRanges: { start: number; end: number }[] = [];
    let activeRangeStart: number | null = null;
    let activeRangeEnd: number | null = null;
    // The furthest point reached since the last backward seek. This is what
    // hls.js's back-buffer eviction trails, unlike the end of a merged played
    // range, which can be far ahead of the playhead after a rewatch.
    let furthestSinceBackwardSeek = video.currentTime;
    let frameCallback: number | undefined;
    let hasFrame = false;
    let closed = false;
    const isPlayed = (time: number) =>
      playedRanges.some(({ start, end }) => start <= time && time <= end);
    const closeActiveRange = () => {
      if (activeRangeStart !== null && activeRangeEnd !== null) {
        const start = Math.min(activeRangeStart, activeRangeEnd);
        const end = Math.max(activeRangeStart, activeRangeEnd);
        playedRanges = sorted(
          [...playedRanges, { start, end }],
          (range) => range.start
        ).reduce<{ start: number; end: number }[]>((merged, range) => {
          const last = merged[merged.length - 1];
          if (last && range.start <= last.end) {
            return [
              ...merged.slice(0, -1),
              { ...last, end: Math.max(last.end, range.end) },
            ];
          }
          return [...merged, range];
        }, []);
      }
      activeRangeStart = null;
      activeRangeEnd = null;
    };
    const recordProgress = (time: number) => {
      activeRangeStart ??= time;
      activeRangeEnd = time;
    };
    const recordPlayhead = (time: number) => {
      recordProgress(time);
      furthestSinceBackwardSeek = Math.max(furthestSinceBackwardSeek, time);
    };
    let quality: string | undefined;
    const downloadsAwaitingPlayback = new Map<string, LoaderStats[]>();
    const countDownload = (stats: LoaderStats) => {
      measurements.segmentCount += 1;
      measurements.segmentBytes += stats.loaded;
      measurements.segmentLoadMs += stats.loading.end - stats.loading.start;
      measurements.segmentFirstDataMs += Math.max(
        0,
        stats.loading.first - stats.loading.start
      );
    };
    const accountHold = () => {
      if (holdStartedAt !== null) {
        measurements.sharedHoldMs += performance.now() - holdStartedAt;
        holdStartedAt = performance.now();
      }
    };
    const updateHold = () => {
      accountHold();
      const held =
        !document.hidden &&
        !video.ended &&
        hasFrame &&
        !seek &&
        context.state === "buffering";
      if (held && holdStartedAt === null) {
        measurements.sharedHoldCount += 1;
        holdStartedAt = performance.now();
      } else if (!held) {
        holdStartedAt = null;
      }
    };
    const collect = (): CameraPlayback => {
      accountHold();
      const now = performance.now();
      const playback = {
        ...measurements,
        camera,
        quality,
        attributes,
        holdCausedMs: context.takeHoldCauseMs(),
        seekPendingMs: seek ? now - seek.startedAt : null,
        startupPendingMs:
          startupStartedAt === null ? null : now - startupStartedAt,
      };
      measurements = emptyMeasurements();
      return playback;
    };
    let registration = initialContext.report.register(collect);
    initialContext.report.setPlaybackRate(context.playbackRate);
    const finishStartup = (outcome: "displayed" | "abandoned") => {
      if (startupStartedAt === null) return;
      const waitedMs = performance.now() - startupStartedAt;
      if (outcome === "displayed") measurements.firstFrameMs.push(waitedMs);
      else measurements.startupWaitMs.push(waitedMs);
      startupStartedAt = null;
    };
    // Classifies a finished scrub chain by its final target against where it
    // started, once — not per intermediate step — so a drag that wobbles
    // back and forth before settling counts as one seek, by where it ends.
    const classifyBackwardSeek = (fromTime: number, target: number) => {
      if (target >= fromTime) return;
      measurements.seekBackwardCount += 1;
      const distance = furthestSinceBackwardSeek - target;
      if (!isPlayed(target)) {
        measurements.seekBackwardUnplayedCount += 1;
      } else {
        for (const seconds of REPLAY_BUCKET_SECONDS) {
          if (distance <= seconds)
            measurements.seekBackwardReplaySeconds[seconds] += 1;
        }
      }
      furthestSinceBackwardSeek = target;
    };
    const abandonWaits = () => {
      finishStartup("abandoned");
      if (seek) {
        measurements.seekAbandonedCount += 1;
        classifyBackwardSeek(seek.fromTime, seek.target);
      }
      seek = null;
    };
    const frameDisplayed = () => {
      if (document.hidden || video.seeking) return;
      hasFrame = true;
      finishStartup("displayed");
      if (seek) {
        const { startedAt: seekStartedAt, fromTime, target } = seek;
        measurements.seekMs.push(performance.now() - seekStartedAt);
        classifyBackwardSeek(fromTime, target);
        seek = null;
      }
      recordPlayhead(video.currentTime);
      updateHold();
    };
    const requestFrame = () => {
      if (frameCallback !== undefined)
        capabilities.cancelVideoFrameCallback?.(frameCallback);
      if (capabilities.requestVideoFrameCallback) {
        frameCallback = capabilities.requestVideoFrameCallback(() => {
          frameCallback = undefined;
          frameDisplayed();
        });
      } else if (video.readyState >= 2) {
        frameDisplayed();
      }
    };
    const loadedFrame = () => {
      if (!capabilities.requestVideoFrameCallback) frameDisplayed();
    };
    const visibilityChanged = () => {
      updateHold();
      if (document.hidden) {
        abandonWaits();
      } else {
        if (!hasFrame) startupStartedAt = performance.now();
        requestFrame();
      }
    };
    const pageHidden = () => {
      accountHold();
      holdStartedAt = null;
      abandonWaits();
    };
    sessionRef.current = {
      updateContext: () => {
        const next = contextRef.current;
        if (!next.playbackContext) return;
        context.report.setPlaybackRate(next.playbackRate);
        context = { ...next.playbackContext, playbackRate: next.playbackRate };
        updateHold();
      },
      trackSeek: (target) => {
        if (document.hidden) return;
        const end = Number.isFinite(video.duration) ? video.duration : Infinity;
        const clampedTarget = Math.min(Math.max(target, 0), end);
        if (clampedTarget === video.currentTime && video.readyState >= 2)
          return;
        if (seek) measurements.seekAbandonedCount += 1;
        // Classify by where this scrub chain started, not `video.currentTime`,
        // which mid-scrub is the previous scrub step rather than where
        // playback actually was before the user started dragging.
        const fromTime = seek ? seek.fromTime : video.currentTime;
        closeActiveRange();
        accountHold();
        holdStartedAt = null;
        seek = {
          startedAt: performance.now(),
          fromTime,
          target: clampedTarget,
        };
      },
      observeHls: (hls) => {
        hls.on(Hls.Events.LEVEL_SWITCHED, (_event, { level }) => {
          if (closed) return;
          const switchedTo = hls.levels[level]?.name;
          const normalisedQuality = switchedTo === "" ? undefined : switchedTo;
          if (quality !== undefined && normalisedQuality !== quality) {
            registration.release(collect());
            registration = initialContext.report.register(collect);
          }
          quality = normalisedQuality;
          if (normalisedQuality === undefined) return;
          downloadsAwaitingPlayback
            .get(normalisedQuality)
            ?.forEach(countDownload);
          downloadsAwaitingPlayback.clear();
        });
        hls.on(Hls.Events.FRAG_LOADED, (_event, { frag, part }) => {
          if (closed || document.hidden) return;
          const stats = part?.stats ?? frag.stats;
          recordPageDownload({
            start: stats.loading.start,
            end: stats.loading.end,
            bytes: stats.loaded,
          });
          const fragmentQuality = hls.levels[frag.level]?.name;
          if (
            quality === undefined ||
            fragmentQuality === undefined ||
            fragmentQuality === quality
          ) {
            countDownload(stats);
            return;
          }
          downloadsAwaitingPlayback.set(fragmentQuality, [
            ...(downloadsAwaitingPlayback.get(fragmentQuality) ?? []),
            stats,
          ]);
        });
      },
    };
    const controller = new AbortController();
    const options = { signal: controller.signal };
    if (capabilities.requestVideoFrameCallback) requestFrame();
    video.addEventListener("loadeddata", loadedFrame, options);
    video.addEventListener(
      "seeking",
      () => {
        // Covers every seek, whatever triggered it (a tracked scrub via
        // `trackSeek`, or a direct `currentTime` assignment elsewhere, such
        // as a drift-correction hard seek), so the played range and replay
        // distance stay accurate even for seeks `trackSeek` never saw. For a
        // tracked seek, `trackSeek` has already closed the range before the
        // browser fires this event, so `previousPosition` is null here and
        // `classifyBackwardSeek` handles `furthestSinceBackwardSeek` once
        // the seek finishes.
        const previousPosition = activeRangeEnd;
        closeActiveRange();
        if (previousPosition !== null) {
          furthestSinceBackwardSeek =
            video.currentTime < previousPosition
              ? video.currentTime
              : Math.max(furthestSinceBackwardSeek, video.currentTime);
        }
        if (capabilities.requestVideoFrameCallback) requestFrame();
      },
      options
    );
    video.addEventListener("seeked", requestFrame, options);
    video.addEventListener(
      "timeupdate",
      () => {
        if (document.hidden || video.seeking) return;
        recordPlayhead(video.currentTime);
      },
      options
    );
    video.addEventListener("ended", updateHold, options);
    document.addEventListener("visibilitychange", visibilityChanged, options);
    window.addEventListener("pagehide", pageHidden, options);
    return () => {
      closed = true;
      controller.abort();
      if (frameCallback !== undefined)
        capabilities.cancelVideoFrameCallback?.(frameCallback);
      abandonWaits();
      registration.release(collect());
      sessionRef.current = null;
    };
  }, [enabled, srcUrl, videoRef]);

  useEffect(() => {
    sessionRef.current?.updateContext();
  }, [playbackContext?.state, playbackRate]);

  const observeHls = useCallback(
    (hls: Hls) => sessionRef.current?.observeHls(hls),
    []
  );
  const trackSeek = useCallback(
    (target: number) => sessionRef.current?.trackSeek(target),
    []
  );
  return { observeHls, trackSeek };
};
